import React, { useContext, type FC, type PropsWithChildren } from 'react';

import { isAbsoluteURL } from '@lucid/utils';
import { handleImagePath } from '@lucid/hooks';

import type { Lucid } from '../../types/types';
import LayoutContext from '../context/layout-context';
import Link, { type LinkProps } from '../link/link';
import type { SharedNavOptions } from './nav';
import Text from '../text/text';
import { Image } from '../image/image';

export interface NavLinkProps extends LinkProps, SharedNavOptions {
  /** Checks if the link is inside the dropdown menu. */
  isInDropdown?: boolean;
  /** Checks if the link has a visio URL param. */
  isVisio?: boolean;
  /** The specific link data. */
  link?: Lucid.Directus.Generated.Menu_Link;
  /** Image associated with the link */
  linkImage?: Lucid.Directus.Generated.Directus_Files;
  /** Link subtext */
  linkSubtext?: string;
  /** Allows to remove link from tabindex */
  shouldRemoveFromTabIndex?: boolean;
}

const NavLink: FC<PropsWithChildren<NavLinkProps>> = ({
  className,
  isInDropdown = false,
  isSuperNav = false,
  isVisio = false,
  link,
  linkImage,
  linkSubtext,
  shouldRemoveFromTabIndex = false,
  variant = 'subNavLink',
  ...rest
}) => {
  const { shortCode } = useContext(LayoutContext);
  const visioQueryParams = '?type=visio';

  // TODO Directus - added this as some links didn't get the final_url created for some reason - fix final_url
  let url = link?.final_url;
  if (!url) return null;
  const shouldDisplayLinkAsButton = link?.display_as_button;
  const linkTitle = link?.default_link_text;

  url = isVisio && link?.beacon_id === 'pricing' ? `${url}${visioQueryParams}` : url;

  if (url?.includes('lucidchart.zendesk.com') && shortCode !== 'en') {
    url = url.replace('/en-us', `/${shortCode}`);
  }

  if (isSuperNav && url) {
    // checks if the top nav url is relative or absolute
    // if it is relative, it adds the lucid.co domain to it.
    if (!isAbsoluteURL(url)) {
      url = `https://lucid.co${url}`;
    }
  }

  const hasImg = !!linkImage?.filename_disk;

  return (
    <Link
      {...rest}
      beaconEvent={
        {
          name: 'clickedMarketingPageElement',
          elementLocation: isInDropdown ? 'nav-sub' : 'nav',
          elementDetail: linkTitle,
          elementType: 'link',
        } as Lucid.Beacon.Event
      }
      css={theme => [
        isInDropdown && {
          label: 'subNavLink',
          maxWidth: '100%',
          '&:active, &:hover, &:focus': {
            color: theme.colors.coolGrey[80],
            '&::after': {
              zIndex: '-1',
              content: '""',
              width: 'calc(100% + 12px)',
              position: 'absolute',
              left: '-12px',
              top: 0,
              height: '100%',
              background: theme.colors.coolGrey[5],
              borderRadius: 'inherit',
            },
          },
        },
        !isInDropdown && [
          {
            label: 'navLink',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          },
        ],
        !shouldDisplayLinkAsButton && [
          {
            '&:active, &:hover, &:focus': {
              color: theme.colors.coolGrey[80],
            },
          },
        ],
        [
          hasImg && {
            '& > img': {
              marginTop: '12px',
            },
            '& > p': {
              marginTop: '12px',
              fontSize: theme.fontSizes.fs12,
              display: 'block',
            },
            flexDirection: 'column',
            height: 'unset',
            alignItems: 'unset',
            outlineWidth: '2px',
            outlineOffset: '-2px',
            '&:active, &:hover, &:focus': {
              color: theme.colors.coolGrey[80],
              '& > p': {
                color: theme.colors.coolGrey[100],
                fontWeight: theme.fontWeights.slightlyStrong,
              },
            },
            '&:focus': {
              outlineColor: theme.colors.coolGrey[60],
            },
          },
          {
            '&:focus': {
              outlineColor: theme.colors.blue[60],
            },
          },
        ],
      ]}
      // eslint-disable-next-line react/jsx-sort-props
      className={className}
      displayAsButton={shouldDisplayLinkAsButton || !isInDropdown}
      tabIndex={shouldRemoveFromTabIndex ? -1 : 0}
      to={url}
      variant={variant}
      willAddBeaconParams={isSuperNav}
    >
      {linkImage ? (
        <Image
          css={{
            height: '23px',
            width: '154px',
          }}
          dataElementPageLocation={isSuperNav ? 'superNav' : 'siteNav'}
          imageAlt={''}
          imageSrc={handleImagePath(linkImage.filename_disk)}
        />
      ) : isInDropdown ? (
        <Text as="span" dataElementPageLocation={isSuperNav ? 'superNav' : 'siteNav'}>
          {linkTitle}
        </Text>
      ) : (
        linkTitle
      )}
      {linkImage && linkSubtext && (
        <Text dataElementPageLocation={isSuperNav ? 'superNav' : 'siteNav'} variant={'interactiveXS400'}>
          {linkSubtext}
        </Text>
      )}
    </Link>
  );
};

export { NavLink };
export default NavLink;
